import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  cloneElement,
} from 'react';
import { useRouter } from 'next/router';
import { GoogleAnalytics } from '@next/third-parties/google';

import styled, { createGlobalStyle } from 'styled-components';
import Header from '../../components/ui/Header';
import Nav from './Nav/Nav';
import Hamburger from '../../components/ui/Hamburger';
import HeaderCta from '../../components/ui/HeaderCta';
import Border from '../../components/ui/Border';
import CookieMessage from './CookieMessage';
import NewsletterPopup from './NewsletterPopup';
import LangSelector from './LangSelector/LangSelector';
import * as FacebookPixel from '../../utils/facebookPixel';
import * as LinkedInPixel from '../../utils/linkedInPixel';
import * as MicrosoftClarity from '../../utils/microsoftClarity';
import { useLenis } from '../../utils/LenisContext';
import PageLoader from './PageLoader';
import { LayoutBottomLeftProvider } from './LayoutBottomLeftContext';
import { ScrollIncentive } from '../ui/ScrollIncentive';

import type { SharedTProps } from '../../utils/LangContext';

interface LayoutProps {
  t: Pick<SharedTProps, 'nav' | 'cookie_message'>;
  hasLoaded: boolean;
  children: React.ReactElement;
  /**
   * Optional floating action rendered on selected routes once `hasLoaded`.
   * Studio uses this to mount its WhatsApp button; podcast leaves it unset
   * so nothing is rendered.
   */
  whatsAppButton?: React.ReactNode;
  scrollIncentiveRoutes?: string[];
  whatsAppButtonRoutes?: string[];
  /**
   * Mounts <NewsletterPopup /> for this app. The popup gates itself
   * with a cookie (30-day expiry on close), so the parent only needs
   * to opt in. Podcast passes `true` (popup may appear on any podcast
   * page); studio leaves it unset.
   */
  enableNewsletterPopup?: boolean;
  /**
   * Tracking IDs supplied per-app from each `_app.tsx`. Empty/undefined
   * skips initialization for that tracker. See `utils/constants.ts` for
   * the canonical values per app.
   */
  fbPixelId?: string;
  linkedInPixelId?: string;
  gaId?: string;
  clarityProjectId?: string;
  /**
   * Studio-only Meta Pixel event. When enabled, individual `/work/*` pages
   * emit `ViewContent` in addition to the generic `PageView`.
   */
  trackCaseStudyViewContent?: boolean;
  /**
   * Landing-page mode for the logo: renders it as plain brand mark (no
   * link to acueducto.com, no scroll-to-top). Combine with `headerCta`
   * to keep the page free of escape routes other than the footer.
   */
  unlinkLogo?: boolean;
  /**
   * Renders arbitrary content in place of the Hamburger + Nav overlay,
   * pinned top-right on desktop / bottom-right on mobile. Pass anything
   * — usually a `Button` linking to the conversion goal, but a badge,
   * lang switcher, or status pill all work. Hamburger and Nav are
   * suppressed automatically when this is set.
   */
  headerCta?: React.ReactNode;
  /**
   * Suppresses Hamburger + Nav without mounting `headerCta`. Use on
   * inner funnel pages where neither the menu nor the CTA make sense
   * (e.g. the user is already on the page the CTA points to).
   */
  hideNav?: boolean;
  /**
   * Suppresses the cookie consent banner. Opt out on apps that have
   * their own consent flow (e.g. the calculator captures email via
   * mailing + reCAPTCHA and doesn't need the generic banner).
   */
  hideCookieMessage?: boolean;
}

interface ChildProps {
  setTitle: (title: string) => void;
  hasLoaded: boolean;
  locale: string;
}

const isCaseStudyRoute = (route: string) => route.startsWith('/work/');

const getCaseStudySlug = (route: string) =>
  route.split('/').filter(Boolean).pop() || 'case-study';

const Layout = ({
  t,
  hasLoaded,
  children,
  whatsAppButton,
  scrollIncentiveRoutes = [],
  whatsAppButtonRoutes = ['/'],
  enableNewsletterPopup,
  fbPixelId,
  linkedInPixelId,
  gaId,
  clarityProjectId,
  trackCaseStudyViewContent,
  unlinkLogo,
  headerCta,
  hideNav,
  hideCookieMessage,
}: LayoutProps) => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const [headerTitle, setTitle] = useState<string>('');
  const [isLangSelectorVisible, setLangSelectorVisible] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const loaderStartRef = useRef<number | null>(null);
  const lastTrackedFacebookPathRef = useRef<string | null>(null);
  const router = useRouter();
  const { stopScroll, startScroll } = useLenis();

  const initializePixels = useCallback((): void => {
    if (fbPixelId) {
      FacebookPixel.initFacebookPixel(fbPixelId);
    }
    if (linkedInPixelId) {
      LinkedInPixel.initLinkedinPixel(linkedInPixelId);
      LinkedInPixel.trackPageView();
    }
    if (clarityProjectId) {
      MicrosoftClarity.initMicrosoftClarity(clarityProjectId);
    }
  }, [fbPixelId, linkedInPixelId, clarityProjectId]);

  useEffect(() => {
    if (hasLoaded) {
      initializePixels();
    }
  }, [hasLoaded, initializePixels]);

  useEffect(() => {
    if (!hasLoaded || !fbPixelId) return;
    if (lastTrackedFacebookPathRef.current === router.asPath) return;

    lastTrackedFacebookPathRef.current = router.asPath;
    FacebookPixel.trackPageView();

    if (trackCaseStudyViewContent && isCaseStudyRoute(router.route)) {
      const caseStudySlug = getCaseStudySlug(router.route);
      FacebookPixel.trackCaseStudyView(caseStudySlug);
    }
  }, [
    router.asPath,
    router.route,
    hasLoaded,
    fbPixelId,
    trackCaseStudyViewContent,
  ]);

  useEffect(() => {
    if (isOpen) {
      stopScroll();
    } else if (hasLoaded) {
      startScroll();
    }
  }, [isOpen, hasLoaded, stopScroll, startScroll]);

  // Mostrar loader al iniciar cambio de idioma
  const handleLanguageChangeStart = () => {
    setShowLoader(true);
    // Guarda el tiempo de inicio
    loaderStartRef.current = Date.now();
  };

  // Ocultar loader cuando termine el cambio de ruta
  useEffect(() => {
    const handleRouteChangeComplete = () => {
      const minDuration = 800;
      const start = loaderStartRef.current ?? Date.now();
      const elapsed = Date.now() - start;
      if (elapsed < minDuration) {
        setTimeout(() => setShowLoader(false), minDuration - elapsed);
      } else {
        setShowLoader(false);
      }
    };
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router.events]);

  // Habilitar Lenis cuando la página haya cargado
  useEffect(() => {
    if (hasLoaded) {
      startScroll();
    }
  }, [hasLoaded, startScroll]);

  // Bloquear scroll con Lenis cuando el loader esté activo
  useEffect(() => {
    if (showLoader) {
      stopScroll();
    } else if (hasLoaded) {
      startScroll();
    }
  }, [showLoader, hasLoaded, stopScroll, startScroll]);

  const toggleNav = useCallback((): void => {
    // Mantener el cierre del lang selector aquí (no en un effect) evita
    // un render en cascada (react-hooks/set-state-in-effect).
    setLangSelectorVisible(false);
    setOpen((prev) => !prev);
  }, []);
  const closeNav = useCallback((): void => setOpen(false), []);

  return (
    <LayoutBottomLeftProvider
      route={router.route}
      scrollIncentiveRoutes={scrollIncentiveRoutes}
      whatsAppButtonRoutes={whatsAppButtonRoutes}
    >
      <LayoutWrapper id="LayoutWrapper">
        <PageLoader visible={showLoader} />
        <Border />
        {headerCta ? (
          <HeaderCta hasLoaded={hasLoaded}>{headerCta}</HeaderCta>
        ) : hideNav ? null : (
          <Hamburger
            toggleNav={toggleNav}
            isOpen={isOpen}
            hasLoaded={hasLoaded}
          />
        )}
        <Header
          isOpen={isOpen}
          headerTitle={headerTitle}
          hasLoaded={hasLoaded}
          closeNav={closeNav}
          locale={router.locale}
          route={router.route}
          unlinkLogo={unlinkLogo}
        />
        {!headerCta && !hideNav && (
          <Nav
            locale={router.locale}
            nav={t.nav}
            closeNav={closeNav}
            isOpen={isOpen}
          />
        )}
        {cloneElement(children as React.ReactElement<ChildProps>, {
          setTitle,
          hasLoaded,
          locale: router.locale,
        })}
        <LangSelector
          isContentVisible={isLangSelectorVisible}
          setIsContentVisible={setLangSelectorVisible}
          onLanguageChangeStart={handleLanguageChangeStart}
        />
        <ScrollIncentive key={router.route} />
        {hasLoaded && whatsAppButton}
        {!hideCookieMessage && <CookieMessage t={t} hasLoaded={hasLoaded} />}
        <BodyOverflow $hasLoaded={hasLoaded} />
        {enableNewsletterPopup && <NewsletterPopup />}
        {gaId && <GoogleAnalytics gaId={gaId} />}
      </LayoutWrapper>
    </LayoutBottomLeftProvider>
  );
};

export default Layout;

const BodyOverflow = createGlobalStyle<{ $hasLoaded: boolean }>`
  .TopBar div {
     box-shadow: 1px 1px 4px ${(props) => props.theme.colors.accent} !important;
  }
  body  {
    overflow-y: ${(props) => (props.$hasLoaded ? 'auto' : 'hidden')};
    overflow-x: hidden;
  }  
`;

const LayoutWrapper = styled.div`
  position: relative;
  width: 100%;
  flex-direction: column;
  display: flex;
  justify-content: flex-start;
  color: ${(props) => props.theme.colors.foreground};
  background-color: ${(props) => props.theme.colors.background};

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    background: transparent;
    margin: 50px 0;
  }
  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #1744c9;
    border-radius: 50px;
  }
  /* Hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #1744c9;
  }
`;
